import {client, waitForApolloCacheRestore} from '@core/GraphQLProvider';
import ProductCatalogService from '@services/ProductCatalogService';
import {createSearchContext} from '@ultradent/upi-fuzzy-search';
import {isNotMarketingMaterial, isNotSample} from '@components/search/searchFilters';
import {
    AutoCompleteSuggestion,
    GetRecentSearches,
    RemoveRecentSearch,
    SaveRecentSearch
} from './query.graphql';

export const DEFAULT_AUTO_COMPLETE_TOP = 10;
export const DEFAULT_RECENT_SEARCH_MATCH_LIMIT = 3;
export const DEFAULT_PRODUCT_SUGGESTION_TOP = 5;

let catalogSearchContextsPromise = null;

function normalizeQuery ( query ) {
    return String( query || '' ).trim().toLowerCase();
}

function getSuggestionText ( suggestion ) {
    return suggestion && (suggestion.queryPlusText || suggestion.text || '');
}

function toRecentSuggestion ( recentSearch ) {
    return {
        __typename: 'AutoCompleteSuggestion',
        text: '',
        queryPlusText: recentSearch.query,
        isRecentSearch: true,
        normalizedQuery: recentSearch.normalizedQuery,
        createdAt: recentSearch.createdAt
    };
}

function getCatalogSearchContexts () {
    if ( catalogSearchContextsPromise ) {
        return catalogSearchContextsPromise;
    }

    catalogSearchContextsPromise = waitForApolloCacheRestore()
        .then( () => ProductCatalogService.getAllForSearch() )
        .then( catalog => {
            const searchableCatalog = (catalog || []).filter( ( {isSearchable} ) => isSearchable );
            const contexts = {
                sku: createSearchContext( searchableCatalog, ['sku', 'kitName', 'searchKeywords'] ),
                text: createSearchContext( searchableCatalog, ['kitName', 'searchKeywords', 'sku'] )
            };

            Object.values( contexts ).forEach( context => context.setFilters( [isNotMarketingMaterial, isNotSample] ) );

            return contexts;
        } ).catch( error => {
            catalogSearchContextsPromise = null;
            throw error;
        } );

    return catalogSearchContextsPromise;
}

const SearchService = {
    async getRecentSearches () {
        const resp = await client.query( {
            fetchPolicy: 'cache-first',
            query: GetRecentSearches
        } );

        return resp?.data?.recentSearches || [];
    },

    async saveRecentSearch ( query ) {
        const normalizedQuery = String( query || '' ).trim();

        if ( !normalizedQuery ) {
            return [];
        }

        const resp = await client.mutate( {
            mutation: SaveRecentSearch,
            variables: {query: normalizedQuery}
        } );

        return resp?.data?.saveRecentSearch || [];
    },

    async removeRecentSearch ( query ) {
        const normalizedQuery = String( query || '' ).trim();

        if ( !normalizedQuery ) {
            return [];
        }

        const resp = await client.mutate( {
            mutation: RemoveRecentSearch,
            variables: {query: normalizedQuery}
        } );

        return resp?.data?.removeRecentSearch || [];
    },

    prepareProductSuggestions () {
        return getCatalogSearchContexts();
    },

    async getRecentSearchSuggestions (
        query,
        {top = DEFAULT_RECENT_SEARCH_MATCH_LIMIT} = {}
    ) {
        const normalizedInput = normalizeQuery( query );
        const recentSearches = await this.getRecentSearches();

        return recentSearches
            .filter( recentSearch => !normalizedInput
                || normalizeQuery( recentSearch.normalizedQuery || recentSearch.query ).includes( normalizedInput ) )
            .slice( 0, top )
            .map( toRecentSuggestion );
    },

    async getAutoCompleteSuggestions (
        query,
        {
            top = DEFAULT_AUTO_COMPLETE_TOP,
            signal
        } = {}
    ) {
        const normalizedQuery = String( query || '' ).trim();

        if ( !normalizedQuery ) {
            return [];
        }

        const resp = await client.query( {
            context: signal ? {fetchOptions: {signal}} : undefined,
            fetchPolicy: 'no-cache',
            query: AutoCompleteSuggestion,
            variables: {
                query: normalizedQuery,
                top
            }
        } );
        const seenSuggestions = new Set();

        return (resp?.data?.suggestions?.queries || []).filter( suggestion => {
            const suggestionText = normalizeQuery( getSuggestionText( suggestion ) );

            if ( !suggestionText || seenSuggestions.has( suggestionText ) ) {
                return false;
            }

            seenSuggestions.add( suggestionText );
            return true;
        } );
    },

    async getBestMatchingProducts ( query, {top = DEFAULT_PRODUCT_SUGGESTION_TOP} = {} ) {
        const normalizedQuery = String( query || '' ).trim();

        if ( !normalizedQuery ) {
            return [];
        }

        const contexts = await getCatalogSearchContexts();
        const searchContext = /^\d/.test( normalizedQuery ) ? contexts.sku : contexts.text;
        const {results = []} = searchContext.query( normalizedQuery );

        return results.slice( 0, top );
    }
};

export default SearchService;
